/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import SharesHistoryTooltip from "components/charts/modern/tooltip/SharesHistoryTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import classNames from "components/ui/classNames";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import subMonths from "date-fns/subMonths";
import type Highcharts from "highcharts/highstock";
import type { SeriesBarOptions, SeriesLineOptions } from "highcharts/highstock";
import SharesHistoryQuery from "lib/graphql/queries/SharesHistoryQuery";
import type {
  ISharesHistoryQuery,
  ISharesHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/SharesHistoryQuery.generated";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import useTheme from "components/ui/useTheme";

interface SharesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function SharesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: SharesChartProps) {
  const intl = useIntl();
  const issuer = useRootIssuer();
  const theme = useTheme();
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isRealEstateFund = issuer.__typename === "BrazilianRealEstateFund";
  const [plotType, setPlotType] = useState<"line" | "column">("line");

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = theme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];

  // Series colors for theme
  const lineColor = theme === "dark" ? colors.orange[400] : colors.orange[600];
  const markerFillColor = theme === "dark" ? colors.orange[900] : colors.orange[50];
  const barColor = theme === "dark" ? colors.orange[700] : colors.orange[300];
  const barHoverColor = theme === "dark" ? colors.orange[500] : colors.orange[600];
  const hoverBorderColor = theme === "dark" ? colors.orange[400] : colors.orange[500];
  const hoverFillColor = theme === "dark" ? colors.orange[900] : colors.orange[50];
  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<ISharesHistoryQuery, ISharesHistoryQueryVariables>(
    SharesHistoryQuery,
    {
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );

  const sharesHistory = (loading && !data ? previousData?.sharesHistory : data?.sharesHistory) || [];
  const hasSharesHistory = sharesHistory.length > 0;

  const sortedSharesHistory = [...sharesHistory]
    .filter((node) => node.totalSharesOutstanding !== null)
    .sort((a, b) => compareAsc(parseISO(a.asOfDate), parseISO(b.asOfDate)));
  const latestSharesNode = sortedSharesHistory[sortedSharesHistory.length - 1] ?? null;
  const latestSharesCount = latestSharesNode?.totalSharesOutstanding ?? null;

  // Variação % dos últimos 12 meses em Ações/Cotas Emitidas
  const twelveMonthsChangePercent = (() => {
    if (!latestSharesNode || latestSharesCount === null) {
      return null;
    }
    const cutoffDate = subMonths(parseISO(latestSharesNode.asOfDate), 12);
    const referenceNode = [...sortedSharesHistory]
      .reverse()
      .find((node) => compareAsc(parseISO(node.asOfDate), cutoffDate) <= 0);
    if (!referenceNode || referenceNode.totalSharesOutstanding === null || referenceNode.totalSharesOutstanding === 0) {
      return null;
    }
    return ((latestSharesCount - referenceNode.totalSharesOutstanding) / referenceNode.totalSharesOutstanding) * 100;
  })();

  const compactFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const series: (SeriesLineOptions | SeriesBarOptions)[] = [
    {
      showInNavigator: true,
      type: plotType === "line" ? "line" : "bar",
      animation: false,
      name: selectedTickerSymbol,
      data: sharesHistory
        .filter((node) => node.totalSharesOutstanding !== null)
        .map((node) => ({
          x: parseISO(node.asOfDate).getTime(),
          y: node.totalSharesOutstanding,
          custom: node,
        }))
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: markerFillColor,
        secondaryColor: lineColor,
      },
    },
  ];

  const labels = {
    // eslint-disable-next-line no-nested-ternary
    sharesOutstanding: isRealEstateFund
      ? intl.formatMessage({
          defaultMessage: "Cotas Emitidas",
          id: "agUdo7",
        })
      : isBrazilianIssuer
        ? intl.formatMessage({
            defaultMessage: "Ações Emitidas",
            id: "zIW0Bv",
          })
        : intl.formatMessage({
            defaultMessage: "Ações em Circulação",
            id: "psT3CN",
          }),
    buyback: intl.formatMessage({
      defaultMessage: "Recompra",
      id: "f8wqYD",
    }),
  };
  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      dateTimeLabelFormats: {
        millisecond: "%b '%y",
        second: "%b '%y",
        minute: "%b '%y",
        hour: "%b '%y",
        day: "%b '%y",
        week: "%b '%y",
        month: "%b '%y",
        year: "%Y",
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
      events: {
        afterSetExtremes: function (this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    yAxis: {
      type: "linear",
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          return intl.formatNumber(parseFloat(String(this.value)), {
            notation: "compact",
            compactDisplay: "short",
            minimumSignificantDigits: 1,
            maximumSignificantDigits: 4,
          });
        },
      },
      tickWidth: 0,
      showFirstLabel: false,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles).yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof sharesHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "SharesHistoryItem") {
          return "";
        }
        return renderToStaticMarkup(
          <SharesHistoryTooltip label={labels.sharesOutstanding} context={this} intl={intl} />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
      },
      line: {
        color: lineColor,
        lineWidth: 2,
        marker: {
          enabled: sharesHistory.length <= 1,
          fillColor: markerFillColor,
          lineColor: lineColor,
          lineWidth: 2,
          width: 8,
          height: 8,
        },
      },
      bar: {
        color: barColor,
        borderWidth: 1.5,
        borderColor: barColor,
        states: {
          hover: {
            borderColor: barHoverColor,
            color: barHoverColor,
          },
        },
      },
    },
  };

  if (loading && !hasSharesHistory) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            )}

            <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />
          </div>

          {hasSharesHistory && latestSharesCount !== null && (
            <div className="hidden items-center sm:inline-flex">
              <div className="flex flex-col items-end gap-1">
                <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-400">
                  {labels.sharesOutstanding}
                </span>
                <span className="flex items-baseline gap-2 whitespace-nowrap leading-none">
                  <span className="text-sm font-bold tabular-nums" style={{ color: lineColor }}>
                    {compactFormatter.format(latestSharesCount)}
                  </span>
                  {twelveMonthsChangePercent !== null && (
                    <span className="flex items-baseline gap-2">
                      <span
                        aria-hidden="true"
                        className={classNames("size-1 self-center rounded-full", {
                          "bg-emerald-500 dark:bg-emerald-400": twelveMonthsChangePercent > 0,
                          "bg-red-500 dark:bg-red-400": twelveMonthsChangePercent < 0,
                          "bg-slate-300 dark:bg-slate-600": twelveMonthsChangePercent === 0,
                        })}
                      />
                      <span className="flex items-baseline gap-1">
                        <span
                          className={classNames("text-sm font-bold tabular-nums", {
                            "text-emerald-600 dark:text-emerald-400": twelveMonthsChangePercent > 0,
                            "text-red-600 dark:text-red-400": twelveMonthsChangePercent < 0,
                            "text-slate-400 dark:text-slate-400": twelveMonthsChangePercent === 0,
                          })}
                        >
                          {twelveMonthsChangePercent > 0 ? "+" : ""}
                          {intl.formatNumber(
                            twelveMonthsChangePercent,
                            // Small variations (e.g. 0.004%) would round to 0,00% with fixed decimals
                            twelveMonthsChangePercent !== 0 && Math.abs(twelveMonthsChangePercent) < 1
                              ? { maximumSignificantDigits: 2 }
                              : {
                                  minimumFractionDigits: twelveMonthsChangePercent === 0 ? 0 : 2,
                                  maximumFractionDigits: 2,
                                },
                          )}
                          %
                        </span>
                        <span
                          className={classNames("text-[11px] font-medium", {
                            "text-emerald-600 dark:text-emerald-400": twelveMonthsChangePercent > 0,
                            "text-red-600 dark:text-red-400": twelveMonthsChangePercent < 0,
                            "text-slate-400 dark:text-slate-500": twelveMonthsChangePercent === 0,
                          })}
                        >
                          <FormattedMessage
                            defaultMessage="nos Últimos 12M"
                            description="Variação percentual de ações emitidas nos últimos 12 meses"
                            id="s4/e1M"
                          />
                        </span>
                      </span>
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>

        {hasSharesHistory ? (
          <HighchartsChart options={options} className="h-96 xl:h-full" />
        ) : (
          <div className="flex h-96 w-80 items-center justify-center sm:w-full">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}
      </div>
    </OnlyPremiumRender>
  );
}
